'use client'

import type { Language } from 'codegen/types'
import * as React from 'react'
import { Cookies, useCookies } from 'react-cookie'
import invariant from 'tiny-invariant'
import { getCookieDomain } from 'utils/env'
import { LANGUAGE_COOKIE_NAME } from 'constants/cookies'

import { validateLocale, validateStudioLocale } from './helpers'

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
invariant(cookieDomain, 'Cookie domain is not defined inside cf-i18n/cookies')

const path = '/'

/**
 * Cookies a previous WordPress deploy could have written on a narrower domain.
 * One of these shadowing the real cookie caused infinite redirects, so both
 * writers below clear them before writing rather than only the hook — the
 * non-React writer is exported, and a future caller of it should not have to
 * rediscover this.
 */
const STALE_COOKIE_SCOPES = [{ path }, { path, domain: 'staging.creativefabrica.com' }] as const

export const makeUseLangCookie = <TLocale extends string = Language>(
  isValidLocale: (value: unknown) => boolean = validateLocale,
) => {
  return () => {
    const [cookies, setCookie, removeCookie] = useCookies([LANGUAGE_COOKIE_NAME])

    const rawCookie = cookies[LANGUAGE_COOKIE_NAME]
    const langCookie = isValidLocale(rawCookie) ? (rawCookie as TLocale) : null

    const setLangCookie = React.useCallback(
      (lang: TLocale) => {
        STALE_COOKIE_SCOPES.forEach(scope => removeCookie(LANGUAGE_COOKIE_NAME, scope))

        const domain = getCookieDomain(cookieDomain)
        setCookie(LANGUAGE_COOKIE_NAME, lang, { domain, path })
      },
      [setCookie, removeCookie],
    )

    const removeLangCookie = React.useCallback(() => {
      const domain = getCookieDomain(cookieDomain)
      removeCookie(LANGUAGE_COOKIE_NAME, { domain, path })
    }, [removeCookie])

    return [langCookie, setLangCookie, removeLangCookie] as const
  }
}

/**
 * Re-reads `document.cookie` and notifies every language-cookie reader in this
 * tab.
 *
 * `useCookies` reads its snapshot with `doNotUpdate`, so it only refreshes when
 * something calls `update()` — a write in this tab, a Chromium `cookieStore`
 * change event, or this. A write from another tab is otherwise invisible.
 */
export function useRefreshLangCookie(): () => void {
  const cookieApi = useCookies([LANGUAGE_COOKIE_NAME])

  // `useCookies` returns [cookies, set, remove, update]; only the last is wanted
  // here, and consecutive destructuring holes read worse than one index.
  return cookieApi[3]
}

export const useLangCookie = makeUseLangCookie<Language>()

/**
 * Studio and its sister apps (freemode, lumo) support the Language Push batch
 * that marketplace / My Account don't yet. Reading the cookie with the default
 * `useLangCookie` (marketplace-only `validateLocale`) rejects those locales and
 * silently falls back to `defaultLanguage`, even though the cookie itself was
 * set correctly. Use this hook wherever Studio reads/writes the language cookie.
 */
export const useStudioLangCookie = makeUseLangCookie<Language>(validateStudioLocale)

/**
 * Reactive reader for the raw `lang` cookie value — no locale allowlist.
 * Use in contexts that need the user's picked locale for targeting or
 * analytics but should not couple to any product's list of renderable
 * locales. Returns `null` when the cookie is missing or empty.
 */
const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.length > 0

export const useRawLangCookie = makeUseLangCookie<string>(isNonEmptyString)

/**
 * Non-React readers and writers for the `lang` cookie, for boot-time code that
 * runs before React mounts. Same name, domain and path as the hooks above, so the
 * two cannot disagree about which cookie they mean.
 */
export const readStudioLangCookie = (): Language | null => {
  const raw = new Cookies().get(LANGUAGE_COOKIE_NAME, { doNotUpdate: false })

  return validateStudioLocale(raw) ? (raw as Language) : null
}

// Arrow consts, not hoisted declarations: the `invariant` above narrows
// `cookieDomain` away from `undefined`, and a hoisted body would not see it.
export const writeStudioLangCookie = (language: Language): void => {
  const cookies = new Cookies()

  STALE_COOKIE_SCOPES.forEach(scope => cookies.remove(LANGUAGE_COOKIE_NAME, scope))

  cookies.set(LANGUAGE_COOKIE_NAME, language, {
    domain: getCookieDomain(cookieDomain),
    path,
  })
}
